/* eslint-disable max-lines */
import { QuickResolveActionItem, SOPNote } from '@hakimo-ui/hakimo/types';
import { MenuDropdown, ShieldIcon } from '@hakimo-ui/hakimo/ui-elements';
import { Button, Tooltip } from '@hakimo-ui/shared/ui-base';
import { ArrowLeftIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import {
  ChevronDownIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/solid';
import clsx from 'clsx';
import { LastEscalationInfo, LocationNotes, WorkflowItemType } from '../..';
import { getQuickResolveActionLabel } from '../sop-manager/type-guards';
import { SOPNotesAction } from './SOPNotesAction';
import { getZeroToleranceTextColor } from './utils';

interface Props {
  currentStep: number;
  onStepChange?: (step: number) => void;
  isZeroToleranceSite?: boolean;
  showQuickSafeInSOP: boolean;
  onQuickResolve?: (comment: string, action?: QuickResolveActionItem) => void;
  quickResolveActions?: QuickResolveActionItem[];
  currentWorkflowItemType?: WorkflowItemType;
  navigateTo: (val: WorkflowItemType) => () => void;
  locationId: string;
  sopNotes: SOPNote[];
}

export function WorkflowHeader(props: Props) {
  const {
    onStepChange,
    currentStep,
    isZeroToleranceSite,
    showQuickSafeInSOP,
    onQuickResolve,
    quickResolveActions,
    currentWorkflowItemType,
    navigateTo,
    locationId,
    sopNotes,
  } = props;

  const onBackClick = () => {
    onStepChange?.(currentStep - 1);
  };

  const quickSafeButton = (
    <div className="bg-primary-500 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700 text-dark-text flex cursor-pointer items-center gap-2 rounded-md px-3 py-[10px] text-sm font-medium">
      <span className="block xl:hidden">
        <Tooltip text="Safe" position="bottom">
          <ShieldCheckIcon className="-m-2 h-5 w-5" />
        </Tooltip>
      </span>
      <span className="hidden xl:block">Safe</span>
      <ChevronDownIcon className="h-4 w-4" />
    </div>
  );

  const quickSafeActions =
    quickResolveActions?.map((action) => ({
      name: getQuickResolveActionLabel(action),
      onClick: () =>
        onQuickResolve?.(getQuickResolveActionLabel(action), action),
    })) ?? [];

  return (
    <div
      className={clsx(
        'flex h-16 items-center justify-between',
        isZeroToleranceSite && 'bg-alert-600 -mx-5 -my-4 px-5 py-2'
      )}
    >
      <div className="left-0 top-0 flex items-center space-x-2">
        {currentStep > 0 && (
          <Button variant="icon" onClick={onBackClick}>
            <ArrowLeftIcon
              className={clsx(
                'h-5 w-5',
                getZeroToleranceTextColor(isZeroToleranceSite)
              )}
            />
          </Button>
        )}
        {isZeroToleranceSite && (
          <div className="flex items-center gap-2">
            <ShieldIcon className="h-6 w-6 text-white" />
            <span className="font-medium text-white">Zero Tolerance</span>
          </div>
        )}
        {currentWorkflowItemType == WorkflowItemType.ESCALATE && (
          <SOPNotesAction
            notes={sopNotes}
            isZeroToleranceSite={isZeroToleranceSite}
          />
        )}

        {showQuickSafeInSOP && currentStep === 0 && !isZeroToleranceSite && (
          <MenuDropdown
            position="bottom-right"
            items={quickSafeActions}
            menuButton={quickSafeButton}
            useOnlyMenuButtonNode
          />
        )}
      </div>
      <div>
        {currentWorkflowItemType == WorkflowItemType.ESCALATE && (
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-col text-xs">
              <span
                className={clsx(
                  'font-semibold',
                  getZeroToleranceTextColor(isZeroToleranceSite)
                )}
              >
                Last Escalated
              </span>
              <LastEscalationInfo
                locationId={locationId}
                showTooltip={false}
                isZeroToleranceSite={isZeroToleranceSite}
              />
            </div>
            <LocationNotes
              locationId={locationId}
              isZeroToleranceSite={isZeroToleranceSite}
            />
          </div>
        )}
        {currentWorkflowItemType === WorkflowItemType.STEPS && (
          <Button
            variant="error"
            onClick={navigateTo(WorkflowItemType.ESCALATE)}
            className={clsx(
              isZeroToleranceSite &&
                '!text-alert-600 !bg-white font-semibold hover:!bg-gray-100'
            )}
          >
            <span className="block xl:hidden">
              <Tooltip text="Escalate" position="bottom">
                <ExclamationTriangleIcon className="-m-2 h-5 w-5" />
              </Tooltip>
            </span>
            <span className="hidden xl:block">Escalate</span>
          </Button>
        )}
      </div>
    </div>
  );
}

export default WorkflowHeader;
